import { groupIdSchema, upgradeKindSchema } from '@lafayette/content';
import { z } from 'zod';

import { actionTargetsSchema, instanceIdSchema, playerIdSchema } from './commands.js';

/**
 * Events are the only thing that mutates game state. Commands validate intent
 * and resolve randomness; the reducer folds the resulting events. That keeps
 * replay from a seed plus an event log byte-for-byte identical.
 */
export const gameEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('matchStarted'),
    seed: z.string(),
    playerIds: z.array(playerIdSchema),
    drawPile: z.array(instanceIdSchema),
    hands: z.record(playerIdSchema, z.array(instanceIdSchema)),
    firstPlayerId: playerIdSchema,
  }),
  z.object({ type: z.literal('turnStarted'), playerId: playerIdSchema }),
  z.object({
    type: z.literal('cardsDrawn'),
    playerId: playerIdSchema,
    instanceIds: z.array(instanceIdSchema),
    reason: z.enum(['turn', 'action']),
  }),
  z.object({ type: z.literal('drawPileReshuffled'), drawPile: z.array(instanceIdSchema) }),
  z.object({
    type: z.literal('cardBanked'),
    playerId: playerIdSchema,
    instanceId: instanceIdSchema,
  }),
  z.object({
    type: z.literal('propertyPlaced'),
    playerId: playerIdSchema,
    instanceId: instanceIdSchema,
    group: groupIdSchema,
    tenantName: z.string().optional(),
  }),
  z.object({
    type: z.literal('wildcardMoved'),
    playerId: playerIdSchema,
    instanceId: instanceIdSchema,
    from: groupIdSchema,
    to: groupIdSchema,
  }),
  z.object({
    type: z.literal('actionPlayed'),
    playerId: playerIdSchema,
    instanceId: instanceIdSchema,
    definitionId: z.string(),
    targets: actionTargetsSchema,
    doubleRentInstanceIds: z.array(instanceIdSchema),
    affected: z.array(playerIdSchema),
    /** Players who hold a cancel card and therefore get a response window. */
    responders: z.array(playerIdSchema),
    /**
     * What each affected player will owe if this resolves. Fixed when the card
     * is played so the amount can be shown during the response window, and so
     * the number a player sees is the number they are charged.
     */
    amount: z.number().int().min(1).optional(),
  }),
  z.object({
    type: z.literal('cancellationResponded'),
    playerId: playerIdSchema,
    cancelled: z.boolean(),
    cardInstanceId: instanceIdSchema.optional(),
  }),
  z.object({
    type: z.literal('actionResolved'),
    instanceId: instanceIdSchema,
    exempt: z.array(playerIdSchema),
  }),
  z.object({
    type: z.literal('debtCreated'),
    debtId: z.string(),
    debtorId: playerIdSchema,
    creditorId: playerIdSchema,
    amount: z.number().int().min(1),
    reason: z.enum(['action', 'rent']),
  }),
  z.object({
    type: z.literal('assetsTransferred'),
    fromId: playerIdSchema,
    toId: playerIdSchema,
    /** Cards that stay banked on arrival. */
    bankInstanceIds: z.array(instanceIdSchema),
    /** Cards that land in the recipient's property collection. */
    propertyInstanceIds: z.array(z.object({ instanceId: instanceIdSchema, group: groupIdSchema })),
    /** Upgrades the recipient must place or bank. */
    upgradeInstanceIds: z.array(instanceIdSchema),
    /**
     * Upgrades that stay attached through the move, used when a whole complete
     * mall changes hands rather than being dismantled for a payment.
     */
    attachedUpgrades: z
      .array(z.object({ instanceId: instanceIdSchema, group: groupIdSchema }))
      .default([]),
    reason: z.enum(['payment', 'takeProperty', 'swapProperty', 'transferCompleteMall']),
  }),
  z.object({
    type: z.literal('debtSettled'),
    debtId: z.string(),
    paid: z.number().int().min(0),
    shortfall: z.number().int().min(0),
  }),
  z.object({
    type: z.literal('upgradeAttached'),
    playerId: playerIdSchema,
    instanceId: instanceIdSchema,
    group: groupIdSchema,
    upgrade: upgradeKindSchema,
  }),
  z.object({
    type: z.literal('upgradeBanked'),
    playerId: playerIdSchema,
    instanceId: instanceIdSchema,
  }),
  z.object({
    type: z.literal('cardsDiscarded'),
    playerId: playerIdSchema,
    instanceIds: z.array(instanceIdSchema),
    reason: z.enum(['handLimit', 'spent']),
  }),
  z.object({
    type: z.literal('discardRequired'),
    playerId: playerIdSchema,
    count: z.number().int().min(1),
  }),
  z.object({ type: z.literal('endTurnCancelled'), playerId: playerIdSchema }),
  z.object({ type: z.literal('turnEnded'), playerId: playerIdSchema }),
  z.object({
    type: z.literal('matchWon'),
    playerId: playerIdSchema,
    groups: z.array(groupIdSchema),
  }),
]);
export type GameEvent = z.infer<typeof gameEventSchema>;
export type GameEventType = GameEvent['type'];
